import { useEffect, useMemo, useState } from 'react';
import { Calendar, User, Clock, DollarSign } from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, EmptyState, Select, Input,
} from '../components/ui';

/**
 * Admin-only "every booking on the platform" view. Merges court_bookings
 * with coach_bookings and lets admins filter by kind, date range, and
 * status. Heavier rows show the ref, customer, club and total.
 */

type Range = 'today' | 'week' | 'month' | 'all';
type Kind = 'all' | 'court' | 'coach';
type Status = 'all' | 'confirmed' | 'pending_payment' | 'cancelled' | 'completed';

type CourtRow = {
  id: string; kind: 'court';
  when: string; until: string; ref: string;
  total: number; currency: string; status: string;
  court_name: string | null;
  club_name: string | null;
  customer: string | null;
};
type CoachRow = {
  id: string; kind: 'coach';
  when: string; until: string; ref: string;
  total: number; currency: string; status: string;
  coach_name: string | null;
  club_name: string | null;
  customer: string | null;
  lesson: string;
};
type Row = CourtRow | CoachRow;

export default function Bookings() {
  const [range, setRange] = useState<Range>('month');
  const [kind, setKind] = useState<Kind>('all');
  const [status, setStatus] = useState<Status>('all');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const [startISO, endISO] = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);
    // "All" looks back 1y rather than no bound so queries stay sane.
    const days = range === 'today' ? 1 : range === 'week' ? 7 : range === 'month' ? 30 : 365;
    const lookback = range === 'all' ? days : 0;
    start.setDate(start.getDate() - lookback);
    const end = new Date(now);
    end.setDate(end.getDate() + (range === 'all' ? days : days));
    return [start.toISOString(), end.toISOString()];
  }, [range]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const statusFilter = status === 'all'
        ? ['confirmed', 'pending_payment', 'cancelled', 'completed']
        : [status];

      const [courtRes, coachRes] = await Promise.all([
        kind === 'coach' ? Promise.resolve({ data: [] }) : supabase
          .from('court_bookings')
          .select(`
            id, booking_reference, start_at, end_at, status, total_amount, currency,
            courts(id, name),
            clubs(id, name),
            profiles!user_id(full_name)
          `)
          .gte('start_at', startISO).lt('start_at', endISO)
          .in('status', statusFilter)
          .order('start_at', { ascending: false })
          .limit(500),
        kind === 'court' ? Promise.resolve({ data: [] }) : supabase
          .from('coach_bookings')
          .select(`
            id, booking_reference, scheduled_at, ends_at, status, total_amount, currency,
            lesson_type, environment,
            coaches(id, display_name),
            clubs(id, name),
            profiles!user_id(full_name)
          `)
          .gte('scheduled_at', startISO).lt('scheduled_at', endISO)
          .in('status', statusFilter)
          .order('scheduled_at', { ascending: false })
          .limit(500),
      ]);

      const courtRows: CourtRow[] = ((courtRes.data ?? []) as any[]).map((b) => ({
        id: b.id, kind: 'court',
        when: b.start_at, until: b.end_at, ref: b.booking_reference,
        total: Number(b.total_amount ?? 0), currency: b.currency, status: b.status,
        court_name: b.courts?.name ?? null,
        club_name:  b.clubs?.name ?? null,
        customer:   b.profiles?.full_name ?? null,
      }));
      const coachRows: CoachRow[] = ((coachRes.data ?? []) as any[]).map((b) => ({
        id: b.id, kind: 'coach',
        when: b.scheduled_at, until: b.ends_at, ref: b.booking_reference,
        total: Number(b.total_amount ?? 0), currency: b.currency, status: b.status,
        coach_name: b.coaches?.display_name ?? null,
        club_name:  b.clubs?.name ?? null,
        customer:   b.profiles?.full_name ?? null,
        lesson:     `${b.lesson_type.replace('_', ' ')} · ${b.environment}`,
      }));
      const merged: Row[] = [...courtRows, ...coachRows].sort(
        (a, b) => new Date(b.when).getTime() - new Date(a.when).getTime(),
      );
      setRows(merged);
      setLoading(false);
    })();
  }, [kind, status, startISO, endISO]);

  const q = search.trim().toLowerCase();
  const filtered = !q
    ? rows
    : rows.filter((r) =>
        r.ref.toLowerCase().includes(q)
        || r.customer?.toLowerCase().includes(q)
        || r.club_name?.toLowerCase().includes(q)
        || (r.kind === 'coach' && r.coach_name?.toLowerCase().includes(q))
        || (r.kind === 'court' && r.court_name?.toLowerCase().includes(q))
      );

  const totalRevenue = filtered
    .filter((r) => ['confirmed', 'completed'].includes(r.status))
    .reduce((s, r) => s + r.total, 0);

  return (
    <div className="p-8 max-w-6xl">
      <PageHeader
        eyebrow="AceRent admin"
        title="Bookings"
        subtitle="Every booking across every club, filterable by type, status, and date range."
      />

      <div className="flex gap-2 flex-wrap mb-4">
        <Select value={kind}   onChange={(e) => setKind(e.target.value as Kind)}    className="w-44">
          <option value="all">All kinds</option>
          <option value="court">Courts only</option>
          <option value="coach">Lessons only</option>
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value as Status)} className="w-44">
          <option value="all">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="pending_payment">Pending payment</option>
          <option value="cancelled">Cancelled</option>
          <option value="completed">Completed</option>
        </Select>
        <Select value={range} onChange={(e) => setRange(e.target.value as Range)} className="w-44">
          <option value="today">Today</option>
          <option value="week">Next 7 days</option>
          <option value="month">Next 30 days</option>
          <option value="all">Last 365 days</option>
        </Select>
        <div className="flex-1 min-w-[200px]">
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ref, customer, club, coach or court" />
        </div>
      </div>

      <Section
        title={`${filtered.length} booking${filtered.length === 1 ? '' : 's'}`}
        subtitle={`Confirmed revenue: ${filtered[0]?.currency ?? 'AED'} ${Math.round(totalRevenue).toLocaleString()}`}
        className="p-0 overflow-hidden"
      >
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading bookings…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No bookings match"
            description="Widen the date range or drop some filters."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">When</th>
                <th className="text-left px-5 py-3 font-semibold">Kind</th>
                <th className="text-left px-5 py-3 font-semibold">Details</th>
                <th className="text-left px-5 py-3 font-semibold">Club</th>
                <th className="text-left px-5 py-3 font-semibold">Customer</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 200).map((r) => (
                <tr key={`${r.kind}-${r.id}`} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3 whitespace-nowrap">
                    <div className="text-gray-900">{fmtDate(r.when)}</div>
                    <div className="text-xs text-gray-500">{fmtTime(r.when)} – {fmtTime(r.until)}</div>
                  </td>
                  <td className="px-5 py-3">
                    {r.kind === 'court'
                      ? <Badge tone="blue" icon={Clock}>Court</Badge>
                      : <Badge tone="lime" icon={User}>Lesson</Badge>}
                  </td>
                  <td className="px-5 py-3">
                    {r.kind === 'court'
                      ? <div>{r.court_name ?? '—'}</div>
                      : (
                        <div>
                          <div>{r.coach_name ?? '—'}</div>
                          <div className="text-xs text-gray-500 capitalize">{r.lesson}</div>
                        </div>
                      )}
                  </td>
                  <td className="px-5 py-3 text-gray-700">{r.club_name ?? '—'}</td>
                  <td className="px-5 py-3 text-gray-700">{r.customer ?? '—'}</td>
                  <td className="px-5 py-3">
                    <Badge tone={
                      r.status === 'confirmed' ? 'green'
                      : r.status === 'pending_payment' ? 'amber'
                      : r.status === 'completed' ? 'gray'
                      : 'red'
                    }>{r.status.replace('_', ' ')}</Badge>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="text-gray-900 flex items-center justify-end gap-1">
                      <DollarSign className="w-3 h-3 text-gray-400" />
                      {r.currency} {r.total.toFixed(0)}
                    </div>
                    <div className="text-[10px] text-gray-400 font-mono">{r.ref}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {filtered.length > 200 && (
          <div className="text-xs text-gray-500 text-center py-3 border-t border-gray-100">
            Showing first 200 of {filtered.length}. Narrow the filters to see the rest.
          </div>
        )}
      </Section>
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
